import React, { useState, useRef } from 'react';
import './MigrationModal.css';
import { MigrationModalProps, MigrationFormData } from '../types';
import { migrationApi } from '../services/migrationApi';

type StepStatus = 'idle' | 'loading' | 'success' | 'error';

interface StepState {
  status: StepStatus;
  error?: string;
}

const MigrationModal: React.FC<MigrationModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [resultUrl, setResultUrl] = useState<string>('');
  const [migrationResponse, setMigrationResponse] = useState<any>(null);
  const [isMigrationCompleted, setIsMigrationCompleted] = useState<boolean>(false);
  const [migrationId, setMigrationId] = useState<string | null>(null);
  const [migrationMode, setMigrationMode] = useState<'manual' | 'auto'>('auto');
  const [steps, setSteps] = useState<{
    step0: StepState;
    step1: StepState;
    step2: StepState;
    step3: StepState;
  }>({
    step0: { status: 'idle' },
    step1: { status: 'idle' },
    step2: { status: 'idle' },
    step3: { status: 'idle' }
  });
  const [formData, setFormData] = useState<MigrationFormData>({
    source: '',
    destination: '',
    repo_name: '',
    branch_name: '',
    zipFile: null
  });
  const [zipFileError, setZipFileError] = useState<string>('');
  const zipFileInputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleInputChange = (field: 'source' | 'destination' | 'repo_name' | 'branch_name', value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Zip file upload related functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
  };

  const handleZipFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setFormData(prev => ({ ...prev, zipFile: null }));
      setZipFileError('Zip file is required.');
      return;
    }

    // Check if file is a zip file
    const isZipFile = file.type === 'application/zip' || 
                      file.type === 'application/x-zip-compressed' ||
                      file.name.toLowerCase().endsWith('.zip') ||
                      file.name.toLowerCase().endsWith('.7z');
    
    if (!isZipFile) {
      setZipFileError('Please upload a valid ZIP file.');
      setFormData(prev => ({ ...prev, zipFile: null }));
      if (zipFileInputRef.current) {
        zipFileInputRef.current.value = '';
      }
      return;
    }

    setZipFileError('');
    setFormData(prev => ({ ...prev, zipFile: file }));
  };

  const clearZipFile = (): void => {
    setFormData(prev => ({ ...prev, zipFile: null }));
    setZipFileError('');
    if (zipFileInputRef.current) {
      zipFileInputRef.current.value = '';
    }
  };

  const handleMigrationModeChange = (mode: 'manual' | 'auto'): void => {
    if (isSubmitting || isMigrationOngoing()) {
      return; // Prevent mode change during migration
    }
    setMigrationMode(mode);
    
    // Reset all errors and step states
    setSteps({
      step0: { status: 'idle' },
      step1: { status: 'idle' },
      step2: { status: 'idle' },
      step3: { status: 'idle' }
    });
    
    // Reset current step based on mode
    // Manual mode starts at step 0 (Upload), auto mode starts at step 1 (Load)
    setCurrentStep(mode === 'manual' ? 0 : 1);
    
    // Clear migration-related state
    setMigrationId(null);
    setResultUrl('');
    setMigrationResponse(null);
    setIsMigrationCompleted(false);
    
    // Clear zip file when switching to auto mode
    if (mode === 'auto') {
      clearZipFile();
    }
  };

  const updateStepStatus = (step: 'step0' | 'step1' | 'step2' | 'step3', status: StepStatus, error?: string) => {
    setSteps(prev => ({
      ...prev,
      [step]: { status, error }
    }));
  };

  // Helper function to extract error message from API response
  const extractErrorMessage = (error: any): string => {
    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    if (error?.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  };


  const executeMigrationProcess = async (startFromStep: number = 1) => {
    let currentExecutingStep = -1;
    let activeMigrationId = migrationId;
    
    try {
      // Step 0: Upload File (only for manual mode)
      if (migrationMode === 'manual' && startFromStep <= 0) {
        console.log('Starting Step 0: Upload File');
        currentExecutingStep = 0;
        updateStepStatus('step0', 'loading');
        setCurrentStep(0);
        
        if (!formData.zipFile) {
          throw new Error('Zip file is required for upload.');
        }

        const uploadResponse = await migrationApi.uploadFile(formData.zipFile);
        const fileUrl = uploadResponse?.message;

        updateStepStatus('step0', 'success');
        console.log('Step 0 completed: Upload File', fileUrl);
      }

      // Step 1: Load
      if (startFromStep <= 1) {
        console.log('Starting Step 1: Load');
        currentExecutingStep = 1;
        updateStepStatus('step1', 'loading');
        setCurrentStep(1);
        
        const loadResponse = await migrationApi.loadToMongo();
        const newMigrationId = loadResponse?.data;

        if (!newMigrationId || typeof newMigrationId !== 'string') {
          throw new Error('Load step did not return a valid migration ID.');
        }

        activeMigrationId = newMigrationId;
        setMigrationId(newMigrationId);
        updateStepStatus('step1', 'success');
        console.log('Step 1 completed: Load');
      }

      if (!activeMigrationId) {
        throw new Error('Migration ID is missing. Please restart the migration process.');
      }
      
      // Step 2: Transform
      if (startFromStep <= 2) {
        console.log('Starting Step 2: Transform');
        currentExecutingStep = 2;
        updateStepStatus('step2', 'loading');
        setCurrentStep(2);
        await migrationApi.transform(formData.source, formData.destination, activeMigrationId, formData.repo_name, formData.branch_name);
        updateStepStatus('step2', 'success');
        console.log('Step 2 completed: Transform');
      }
      
      // Step 3: Migrate
      if (startFromStep <= 3) {
        console.log('Starting Step 3: Migrate');
        currentExecutingStep = 3;
        updateStepStatus('step3', 'loading');
        setCurrentStep(3);
        
        const response = await migrationApi.migrate(formData.source, formData.destination, activeMigrationId, formData.repo_name, formData.branch_name);
        updateStepStatus('step3', 'success');
        setMigrationResponse(response);
        setIsMigrationCompleted(true);
        try {
          await migrationApi.cleanup();
          console.log('Cleanup completed automatically after migration');
        } catch (cleanupError) {
          console.error('Automatic cleanup failed:', cleanupError);
        }
        // For non-S3 destinations, keep the existing behavior
        if (response.data && typeof response.data === 'string') {
          setResultUrl(response.data);
        } else if (response.data && typeof response.data === 'object' && 'downloadUrl' in response.data) {
          // For S3, the response structure has data.downloadUrl
          const s3Data = response.data as any;
          setResultUrl(s3Data.downloadUrl || '');
        } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          // For S3, the response structure might be nested: data.data.downloadUrl
          const s3Response = response.data as any;
          if (s3Response.data && s3Response.data.downloadUrl) {
            setResultUrl(s3Response.data.downloadUrl);
          }
        }
        console.log('Step 3 completed: Migrate', response);
      }
      
    } catch (error: any) {
      console.error('Migration process failed at step:', currentExecutingStep, error);
      const errorMessage = extractErrorMessage(error);
      try {
        await migrationApi.cleanup();
        console.log('Cleanup completed automatically after migration');
      } catch (cleanupError) {
        console.error('Automatic cleanup failed:', cleanupError);
      }
      // Determine which step failed based on the actual executing step
      if (currentExecutingStep === 0) {
        updateStepStatus('step0', 'error', errorMessage);
      } else if (currentExecutingStep === 1) {
        updateStepStatus('step1', 'error', errorMessage);
      } else if (currentExecutingStep === 2) {
        updateStepStatus('step2', 'error', errorMessage);
      } else if (currentExecutingStep === 3) {
        updateStepStatus('step3', 'error', errorMessage);
      }
    }
  };

  const retryStep = async (stepNumber: number) => {
    setIsSubmitting(true);
    
    try {
      // Reset completion status when retrying
      setIsMigrationCompleted(false);
      
      // Reset steps from the retry point onwards
      if (stepNumber === 0) {
        setSteps(prev => ({
          step0: { status: 'idle' },
          step1: { status: 'idle' },
          step2: { status: 'idle' },
          step3: { status: 'idle' }
        }));
        await executeMigrationProcess(0);
      } else if (stepNumber === 1) {
        setSteps(prev => ({
          ...prev,
          step1: { status: 'idle' },
          step2: { status: 'idle' },
          step3: { status: 'idle' }
        }));
        await executeMigrationProcess(1);
      } else if (stepNumber === 2) {
        setSteps(prev => ({
          ...prev,
          step2: { status: 'idle' },
          step3: { status: 'idle' }
        }));
        await executeMigrationProcess(2);
      } else if (stepNumber === 3) {
        setSteps(prev => ({
          ...prev,
          step3: { status: 'idle' }
        }));
        await executeMigrationProcess(3);
      }
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Validation based on migration mode
    if (!formData.source || !formData.destination || !formData.repo_name || !formData.branch_name) {
      return;
    }
    
    if (migrationMode === 'manual' && !formData.zipFile) {
      setZipFileError('Zip file is required for manual upload.');
      return;
    }

    setIsSubmitting(true);
    
    // Reset all steps
    setSteps({
      step0: { status: 'idle' },
      step1: { status: 'idle' },
      step2: { status: 'idle' },
      step3: { status: 'idle' }
    });
    setResultUrl('');
    setMigrationResponse(null);
    setIsMigrationCompleted(false);
    setMigrationId(null);

    // Execute the migration process
    // Start from Step 0 if manual mode, otherwise start from Step 1
    const startStep = migrationMode === 'manual' ? 0 : 1;
    await executeMigrationProcess(startStep);
    
    setIsSubmitting(false);
  };

  // Check if migration is currently ongoing
  const isMigrationOngoing = (): boolean => {
    return isSubmitting || 
           steps.step0.status === 'loading' ||
           steps.step1.status === 'loading' || 
           steps.step2.status === 'loading' || 
           steps.step3.status === 'loading';
  };

  const handleClose = (): void => {
    // Prevent closing during ongoing migration
    if (isMigrationOngoing()) {
      return;
    }
    
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Store completion status before resetting
    const wasCompleted = isMigrationCompleted;
    
    // Reset all state
    setFormData({ source: '', destination: '', repo_name: '', branch_name: '', zipFile: null });
    setCurrentStep(0);
    setIsSubmitting(false);
    setResultUrl('');
    setMigrationResponse(null);
    setIsMigrationCompleted(false);
    setMigrationId(null);
    setZipFileError('');
    setMigrationMode('auto');
    if (zipFileInputRef.current) {
      zipFileInputRef.current.value = '';
    }
    setSteps({
      step0: { status: 'idle' },
      step1: { status: 'idle' },
      step2: { status: 'idle' },
      step3: { status: 'idle' }
    });
    
    // Call onClose with completion status
    onClose(wasCompleted);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">Initiate New Migration</h2>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={isMigrationOngoing()}
            title={isMigrationOngoing() ? 'Cannot close during migration' : 'Close'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="progress-container">
          <div className="progress-steps">
            {/* Step 0 - Upload (only shown in manual mode) */}
            {migrationMode === 'manual' && (
              <>
                <div className={`step ${currentStep >= 0 ? 'active' : ''} ${steps.step0.status === 'success' ? 'completed' : ''} ${steps.step0.status === 'error' ? 'error' : ''}`}>
                  <div className="step-circle">
                    {steps.step0.status === 'loading' ? (
                      <div className="spinner"></div>
                    ) : steps.step0.status === 'success' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : steps.step0.status === 'error' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <span className="step-number">1</span>
                    )}
                  </div>
                  <span className="step-label">Upload</span>
                  {steps.step0.status === 'error' && (
                    <button className="retry-button" onClick={() => retryStep(0)}>
                      Retry
                    </button>
                  )}
                </div>
                
                <div className={`step-connector ${steps.step0.status === 'success' ? 'active' : ''}`}></div>
              </>
            )}
            
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${steps.step1.status === 'success' ? 'completed' : ''} ${steps.step1.status === 'error' ? 'error' : ''}`}>
              <div className="step-circle">
                {steps.step1.status === 'loading' ? (
                  <div className="spinner"></div>
                ) : steps.step1.status === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : steps.step1.status === 'error' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span className="step-number">{migrationMode === 'manual' ? '2' : '1'}</span>
                )}
              </div>
              <span className="step-label">Load</span>
              {steps.step1.status === 'error' && (
                <button className="retry-button" onClick={() => retryStep(1)}>
                  Retry
                </button>
              )}
            </div>
            
            <div className={`step-connector ${steps.step1.status === 'success' ? 'active' : ''}`}></div>
            
            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${steps.step2.status === 'success' ? 'completed' : ''} ${steps.step2.status === 'error' ? 'error' : ''}`}>
              <div className="step-circle">
                {steps.step2.status === 'loading' ? (
                  <div className="spinner"></div>
                ) : steps.step2.status === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : steps.step2.status === 'error' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span className="step-number">{migrationMode === 'manual' ? '3' : '2'}</span>
                )}
              </div>
              <span className="step-label">Transform</span>
              {steps.step2.status === 'error' && (
                <button className="retry-button" onClick={() => retryStep(2)}>
                  Retry
                </button>
              )}
            </div>
            
            <div className={`step-connector ${steps.step2.status === 'success' ? 'active' : ''}`}></div>
            
            <div className={`step ${currentStep >= 3 ? 'active' : ''} ${steps.step3.status === 'success' ? 'completed' : ''} ${steps.step3.status === 'error' ? 'error' : ''}`}>
              <div className="step-circle">
                {steps.step3.status === 'loading' ? (
                  <div className="spinner"></div>
                ) : steps.step3.status === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : steps.step3.status === 'error' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span className="step-number">{migrationMode === 'manual' ? '4' : '3'}</span>
                )}
              </div>
              <span className="step-label">Migrate</span>
              {steps.step3.status === 'error' && (
                <button className="retry-button" onClick={() => retryStep(3)}>
                  Retry
                </button>
              )}
            </div>
          </div>
          
          {/* Error Message Display */}
          {(steps.step0.status === 'error' || steps.step1.status === 'error' || steps.step2.status === 'error' || steps.step3.status === 'error') && (
            <div className="progress-error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4M12 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>
                {steps.step0.status === 'error' ? steps.step0.error :
                 steps.step1.status === 'error' ? steps.step1.error : 
                 steps.step2.status === 'error' ? steps.step2.error : 
                 steps.step3.status === 'error' ? steps.step3.error : ''}
              </span>
            </div>
          )}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="modal-form">
          {/* Migration Mode Toggle */}
          <div className="migration-mode-toggle-container">
            <label className="migration-mode-label">Migration Mode</label>
            <div className="migration-mode-toggle">
              <button
                type="button"
                className={`toggle-option ${migrationMode === 'auto' ? 'active' : ''}`}
                onClick={() => handleMigrationModeChange('auto')}
                disabled={isSubmitting || isMigrationOngoing()}
              >
                Auto Upload
              </button>
              <button
                type="button"
                className={`toggle-option ${migrationMode === 'manual' ? 'active' : ''}`}
                onClick={() => handleMigrationModeChange('manual')}
                disabled={isSubmitting || isMigrationOngoing()}
              >
                Manual Upload
              </button>
            </div>
          </div>

          <div className="form-fields">
            {/* Source Field */}
            <div className="form-group">
              <label htmlFor="source" className="form-label">
                Source<span className="required">*</span>
              </label>
              <div className="select-container">
                <select
                  id="source"
                  value={formData.source}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  className="form-select"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Source</option>
                  <option value="endevor">Endevor Extract</option>
                  <option value="changeman">Changeman Extract</option>
                  <option value="ispw">ISPW Extract</option>
                </select>
                <div className="select-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Destination Field */}
            <div className="form-group">
              <label htmlFor="destination" className="form-label">
                Destination<span className="required">*</span>
              </label>
              <div className="select-container">
                <select
                  id="destination"
                  value={formData.destination}
                  onChange={(e) => handleInputChange('destination', e.target.value)}
                  className="form-select"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Destination</option>
                  <option value="s3 bucket">S3</option>
                  <option value="github">Github</option>
                  <option value="gitlab">Gitlab</option>
                  <option value="bitbucket">Bitbucket</option>
                  <option value="azure">Azure</option>
                </select>
                <div className="select-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Repo Name Field */}
            <div className="form-group">
              <label htmlFor="repo_name" className="form-label">
                Repo Name<span className="required">*</span>
              </label>
              <input
                id="repo_name"
                type="text"
                value={formData.repo_name}
                onChange={(e) => handleInputChange('repo_name', e.target.value)}
                className="form-input"
                placeholder="Enter repository name"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Branch Name Field */}
            <div className="form-group">
              <label htmlFor="branch_name" className="form-label">
                Branch Name<span className="required">*</span>
              </label>
              <input
                id="branch_name"
                type="text"
                value={formData.branch_name}
                onChange={(e) => handleInputChange('branch_name', e.target.value)}
                className="form-input"
                placeholder="Enter branch name"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Zip File Upload (only shown in manual mode) */}
            {migrationMode === 'manual' && (
              <div className="form-group">
                <label className="form-label">
                  Zip File<span className="required">*</span>
                </label>
                <input
                  type="file"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  ref={zipFileInputRef}
                  className="file-input-hidden"
                  onChange={handleZipFileChange}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className={`file-upload-dropzone ${formData.zipFile ? 'has-file' : ''}`}
                  onClick={() => !isSubmitting && zipFileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  <div className="file-upload-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="file-upload-text">
                    <span className="file-upload-title">
                      {formData.zipFile ? 'Replace uploaded file' : 'Upload ZIP file'}
                    </span>
                    <span className="file-upload-hint">Supports .zip files</span>
                  </div>
                  <div className="file-upload-cta">Browse</div>
                </button>

                {formData.zipFile ? (
                  <div className="file-chip">
                    <div className="file-chip-info">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>
                        {formData.zipFile.name} ({formatFileSize(formData.zipFile.size)})
                      </span>
                    </div>
                    <button
                      type="button"
                      className="remove-file-button"
                      onClick={clearZipFile}
                      disabled={isSubmitting}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}

                <p className="file-helper-text">
                  Upload the ZIP file to proceed with migration.
                </p>

                {zipFileError && <p className="file-error">{zipFileError}</p>}
              </div>
            )}
          </div>

          {/* Migration Result URL */}
          {(resultUrl || migrationResponse) && (
            <div className="migration-result">
              <div className="result-content">
                <h3>Migration Completed Successfully!</h3>
                {formData.destination.toLowerCase() === 's3 bucket' && (migrationResponse?.data?.downloadUrl || migrationResponse?.data?.data?.downloadUrl) ? (
                  <>
                    <p>Your migration has been completed. Click the button below to download the file:</p>
                    <a 
                      href={migrationResponse.data.downloadUrl || migrationResponse.data.data.downloadUrl} 
                      download
                      className="download-button"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Download File
                    </a>
                  </>
                ) : (
                  <>
                    <p>Your migration has been completed. Click the link below to view the results:</p>
                    <a 
                      href={resultUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="result-link"
                    >
                      Open Migration Result
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="modal-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={handleClose}
              disabled={isMigrationOngoing()}
              title={isMigrationOngoing() ? 'Cannot cancel during migration' : 'Cancel'}
            >
              Close
            </button>
            <button 
              type="submit" 
              className="migration-submit-button"
              disabled={isSubmitting || !formData.source || !formData.destination || !formData.repo_name || !formData.branch_name || isMigrationCompleted || (migrationMode === 'manual' && !formData.zipFile)}
            >
              {isSubmitting ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MigrationModal;
